import { useMemo, useState } from 'react'
// import { Link } from 'react-router-dom'  // used only by the commented-out back link
import { useTranslation } from 'react-i18next'
import { ForgeButton } from '@/components/forge/ForgeButton'
// import { IconButton } from '@/components/forge/ForgeButton'  // crew strip only
import { ForgeDialog } from '@/components/forge/ForgeDialog'
import { PlaceFilter } from '@/components/forge/PlaceFilter'
import { Icon } from '@/components/forge/Icon'
import { Panel } from '@/components/forge/Panel'
import { AiBadge, StateBadge } from '@/components/forge/StatusBadge'
import { EmptyState, ErrorState, SkeletonRows } from '@/components/forge/States'
import { Eyebrow, Num } from '@/components/forge/primitives'
import { usePermissionScopes } from '@/features/auth/permissions'
import { useHierarchyChildren } from '@/features/hierarchy/queries'
import { lookFor } from '@/features/shiftPatterns/api'
import {
  useCreatePattern,
  useDeletePattern,
  useShiftPatterns,
  useUpdatePattern,
} from '@/features/shiftPatterns/queries'
import {
  DOW_ORDER,
  blankPattern,
  crossesMidnight,
  overlappingPatterns,
  patternDuration,
  resolveCurrentPattern,
} from '@/features/shiftPatterns/arithmetic'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useToast } from '@/providers/ToastProvider'
import { ON_TINT_CLASS, STRIPE_CLASS, TINT_CLASS } from '@/lib/tone'
import { cn } from '@/lib/cn'
import { ShiftPatternDrawer } from './ShiftPatternDrawer'

/**
 * Settings → Shift Master.
 *
 * LIVE, against `/v1/shifts`. Patterns, their windows, the days they run and
 * the plants they apply to.
 *
 * ── It moved here, and the old reasoning was right at the time ───────────
 * This screen used to sit at `/app/shifts/master`, and its header said why:
 * "everything that reads a pattern is on Shifts and a rota is not a preference".
 * That held while a pattern was session state with nothing behind it. It stopped
 * holding when the pattern became a stored account resource edited by whoever
 * holds `shifts.manage` — which is a configuration screen by every test
 * this codebase applies, and is where the design put it in the first place. The
 * Shifts page's link still works; `/app/shifts/master` redirects.
 *
 * ── What a pattern is for ────────────────────────────────────────────────
 * The times defined here are what resolves an operator's login to a shift, and
 * that resolution pre-fills Log Event and Shift Handover. So the screen's real
 * subject is not the list — it is what goes wrong with one. The demo had three
 * such notices; two survive, and the third is gone for a stated reason:
 *
 *   **Two patterns claiming one minute.** Computed live against the whole set,
 *   on the card and again in the drawer as the times move. The SERVER does not
 *   check this, so the warning is the only thing standing between an account
 *   and an ambiguous login. It warns and still allows the save — an overlap is
 *   occasionally deliberate and the resolution rule is defined.
 *
 *   **A pattern named for hours it does not run.** Now a type mismatch rather
 *   than a name one: a pattern typed `night` running 06:00–14:00 is the same
 *   lie the demo caught by comparing against a preset, and `type` is a stored
 *   field where the preset was a client-side list.
 *
 *   **A window with nobody rostered to it.** GONE. Crew assignment lives on the
 *   USER (`UserShift`, written through `PUT /users/{id}`), and no endpoint
 *   answers "who is on this pattern". The crew strip is commented out on the
 *   card rather than deleted. See BACKEND-GAPS.md.
 *
 * ── Grace is the significant loss ────────────────────────────────────────
 * The design's whole auto-detection story is the grace window: a login a minute
 * before six is the six o'clock shift. There is no column for it, so resolution
 * runs with a grace of zero and the banner no longer promises one. The picker is
 * commented out in the drawer and the arithmetic still takes the parameter.
 *
 * ── Scope is ONE plant, or the account ──────────────────────────────────
 * `plant_id: null` means every plant — an account-level pattern. This was a LIST
 * (`plant_ids`) until the `shift_scopes` table was dropped; see the header of
 * `features/shiftPatterns/api.js` for what changed and why every write was
 * failing until it did.
 *
 * It also makes the screen's controls conditional, which they were not before.
 * An account-level grant may do anything; a plant grant may only touch its own
 * plants' patterns, and sees account-level ones READ-ONLY. So a card decides
 * whether to offer Edit, and the drawer decides whether to offer "every plant" —
 * both from `usePermissionScopes`, mirroring the server's `covers_plant`.
 */

/** Filtering is client-side over the whole set — see `fetchPatterns`. */
const ALL = 'all'

export function ShiftMasterPage() {
  const { t } = useTranslation(['app', 'common'])
  useDocumentTitle(`${t('shifts.master.title')} · ${t('common:brand.name')}`)

  /* One permission covers the whole resource, so the gate is at the page. Not
     authorisation — the API rules on every request — just not offering a screen
     that would answer 403 to everything done with it. */
  /* `shifts.manage`, which is the key the catalogue actually seeds. This said
     `shifts.manage_shifts`, which is in no permission map there has ever been —
     so the gate fell through to its own refusal and every non-root user was
     locked out of a screen they held the permission for. */
  const gate = usePermissionScopes('shifts.manage')

  return (
    <div className="px-8 py-6 max-xl:px-5.5">
      {/* The back link to Shifts is commented out with the move: this is a
          Settings page now and the settings rail is what sits beside it.
          <ForgeButton asChild>
            <Link to="/app/shifts">…{t('shifts.title')}</Link>
          </ForgeButton> */}

      <header className="mb-4">
        <h1 className="m-0 text-headline">{t('shifts.master.title')}</h1>
        <p className="mt-2 mb-0 max-w-[72ch] text-body-sm text-ink-dim">
          {t('shifts.master.subtitle')}
        </p>
      </header>

      {gate.isPending ? (
        <SkeletonRows count={4} />
      ) : gate.granted ? (
        <Patterns grant={gate} />
      ) : (
        <EmptyState
          icon="lock"
          title={t('shifts.master.forbidden')}
          body={t('shifts.master.forbiddenBody')}
        />
      )}
    </div>
  )
}

function Patterns({ grant }) {
  const { t } = useTranslation(['app', 'common'])
  const { toast } = useToast()

  /* The filtered plant, as `PlaceFilter` holds it (with its trail, so the
     picker reopens where it was). `site` is the id the rest of the page reads. */
  const [sitePlace, setSitePlace] = useState(null)
  const site = sitePlace?.id ?? ALL
  /** `blankPattern(site)` for a new one, or the pattern being changed. */
  const [editing, setEditing] = useState(null)
  const [removing, setRemoving] = useState(null)
  /** `{pattern, dirty}` while the status act is being confirmed. */
  const [switching, setSwitching] = useState(null)

  const patterns = useShiftPatterns()
  const create = useCreatePattern()
  const update = useUpdatePattern()
  const remove = useDeletePattern()

  /**
   * Every site in the account.
   *
   * The `plants` collection, which takes no parent filter at all: a plant's
   * parent is the account, and the server scopes it by the token — the same read
   * Asset Classes makes for its scope picker. Used only to NAME a pattern's
   * plant on its card and in the drawer; choosing one goes through `PlaceFilter`
   * and `PlaceField`, which drill the hierarchy themselves.
   */
  const sites = useHierarchyChildren({ tier: 'site', parentId: null, page: 1, pageSize: 200 })
  const siteOptions = useMemo(() => sites.data?.items ?? [], [sites.data])
  const siteName = useMemo(
    () => new Map(siteOptions.map((entry) => [entry.id, entry.name])),
    [siteOptions],
  )

  const all = useMemo(() => patterns.data?.items ?? [], [patterns.data])

  /* Narrowed in the client on purpose. The server has a `plant_id` filter and it
     is the right one for a paged list — but overlap detection and current-shift
     resolution are questions about the WHOLE set, and both are on this screen.
     Fetching a filtered page would make the warnings disagree with themselves
     depending on the filter. */
  const shown = useMemo(
    () =>
      site === ALL
        ? all
        : all.filter((pattern) => pattern.plantId == null || pattern.plantId === site),
    [all, site],
  )

  /**
   * Whether this grant may change one pattern — the client's copy of the
   * server's `covers_plant`.
   *
   * An account-level grant covers everything. A plant grant covers its own
   * plants' patterns and NOT the account-level ones, which it may see and not
   * touch. Getting this wrong in the permissive direction costs a 403 somebody
   * can read; getting it wrong the other way hides a control that works. It is
   * written to match the server exactly rather than to err either way.
   */
  const canEdit = useMemo(
    () => (pattern) =>
      grant.accountLevel ||
      (pattern.plantId != null && grant.plantIds.has(pattern.plantId)),
    [grant],
  )

  /* A plant grant cannot create an account-level pattern, so a new one is seeded
     at a plant. The filtered site when that is one they hold, otherwise the
     first plant they do — never null, which the server would refuse. */
  const seedFor = (chosen) => {
    const blank = blankPattern(chosen)
    if (grant.accountLevel || blank.plantId != null) return blank
    const [first] = grant.plantIds
    return { ...blank, plantId: first ?? null }
  }

  /* What a login right now would resolve to. Real: it reads the clock against
     the live list, so turning a pattern off or moving its hours changes the
     sentence. The prototype prints the same one either way. */
  const current = resolveCurrentPattern(site === ALL ? null : site, all)

  function save(next) {
    const mutation = next.id ? update : create
    mutation.mutate(next, {
      onSuccess: (saved) => {
        toast(
          t(next.id ? 'shifts.master.toast.updated' : 'shifts.master.toast.created', {
            name: saved.name,
          }),
          'ok',
        )
        setEditing(null)
      },
      onError: () => toast(t('shifts.master.toast.saveFailed'), 'error'),
    })
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-x-4 gap-y-2.5">
        <h2 className="m-0 text-title">{t('shifts.master.management')}</h2>
        <div className="flex flex-wrap items-end gap-2">
          {/* The shared "choose a place" dialog, limited to plants. The account
              row stays choosable — it is how the filter goes back to every
              site. */}
          <PlaceFilter
            id="pattern-site"
            value={sitePlace}
            onChange={setSitePlace}
            allowTiers={SITE_FILTER_TIERS}
            label={t('shifts.master.field.site')}
            emptyLabel={t('shifts.master.allSites')}
            note={t('shifts.master.filterNote')}
          />
          <ForgeButton
            variant="primary"
            disabled={patterns.isPending}
            onClick={() => setEditing(seedFor(site))}
          >
            <Icon name="plus" size="xs" />
            {t('shifts.master.add')}
          </ForgeButton>
        </div>
      </div>

      {/* Auto-detection. Violet because the sentence is Forge AI's resolution of
          a login to a shift, not a routine status line. */}
      <Panel stripe="violet" className="mb-4 flex flex-wrap items-center gap-3">
        <AiBadge label={t('shifts.master.detectionLabel')} />
        <p className="m-0 min-w-0 flex-1 text-body-sm text-ink-dim">
          {t('shifts.master.detection')}{' '}
          {current ? (
            <Num className="font-semibold text-ink">
              {t('shifts.master.detectionActive', {
                name: current.name,
                start: current.start,
                end: current.end,
              })}
            </Num>
          ) : (
            <span className="text-ink-mute">{t('shifts.master.detectionNone')}</span>
          )}
        </p>
      </Panel>

      {patterns.isPending ? (
        <SkeletonRows count={4} />
      ) : patterns.isError ? (
        <ErrorState
          error={patterns.error}
          title={t('shifts.master.loadFailed')}
          onRetry={patterns.refetch}
        />
      ) : (
        <>
          <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(min(100%,17rem),1fr))]">
            {shown.map((pattern) => (
              <PatternCard
                key={pattern.id}
                pattern={pattern}
                patterns={all}
                siteName={siteName}
                editable={canEdit(pattern)}
                onEdit={() => setEditing(pattern)}
              />
            ))}

            <button
              type="button"
              onClick={() => setEditing(seedFor(site))}
              className="flex min-h-45 flex-col items-center justify-center gap-2 rounded-sm border border-dashed border-hairline p-4 text-center transition-colors hover:border-forge"
            >
              <span className="flex size-9 items-center justify-center rounded-full bg-sunken">
                <Icon name="plus" size="sm" className="text-ink-mute" />
              </span>
              <span className="text-body-sm font-semibold">{t('shifts.master.addTile')}</span>
              <span className="text-micro text-ink-mute">{t('shifts.master.addTileSub')}</span>
            </button>
          </div>

          {/* Only when the filter emptied it. The add tile above is always
              there, so a genuinely empty account is never a blank screen. */}
          {shown.length === 0 && site !== ALL ? (
            <p className="mt-3 mb-0 text-body-sm text-ink-dim">
              {t('shifts.master.noneAtSite')}
            </p>
          ) : null}

          {patterns.data?.truncated ? (
            <p className="mt-3 mb-0 text-body-sm text-on-amber">
              {t('shifts.master.truncated', { total: patterns.data.total })}
            </p>
          ) : null}
        </>
      )}

      {editing ? (
        <ShiftPatternDrawer
          // Keyed so opening a second pattern remounts the form rather than
          // showing the first one's values under the second one's title.
          key={editing.id ?? 'new'}
          open
          pattern={editing}
          patterns={all}
          /* For NAMING a plant only — the drawer picks one through
             `PlaceField` and checks the pick against the grant itself. */
          sites={siteOptions}
          grant={grant}
          saving={create.isPending || update.isPending}
          onClose={() => setEditing(null)}
          onSave={save}
          onDelete={(id) => setRemoving(all.find((entry) => entry.id === id) ?? null)}
          /* The stored record, not the form: the status act applies on its own
             and does not carry an unsaved rename with it. `dirty` says whether
             there is one, so the confirmation can mention it. */
          onToggleActive={(dirty) => setSwitching({ pattern: editing, dirty })}
        />
      ) : null}

      {/* Turning a pattern on or off, applied on its own.
          ── Why it confirms ──────────────────────────────────────────────
          Deactivating is how a shift is retired, and everything that reads a
          pattern stops resolving to it the moment it happens: Log Event and
          Shift Handover no longer offer it, and a login in its window resolves
          to whatever else claims that minute, or to nothing. That is not an
          outcome a checkbox should produce in passing.

          Reversible, unlike Delete, which is why it is an ordinary button and
          not a danger one. */}
      <ForgeDialog
        open={Boolean(switching)}
        onOpenChange={(open) => (open ? null : setSwitching(null))}
        title={t(
          switching?.pattern.active
            ? 'shifts.master.deactivateTitle'
            : 'shifts.master.activateTitle',
          { name: switching?.pattern.name ?? '' },
        )}
        description={[
          t(
            switching?.pattern.active
              ? 'shifts.master.deactivateBody'
              : 'shifts.master.activateBody',
          ),
          switching?.dirty ? t('shifts.master.unsavedLost') : null,
        ]
          .filter(Boolean)
          .join(' ')}
        footer={
          <>
            <ForgeButton onClick={() => setSwitching(null)}>
              {t('common:actions.cancel')}
            </ForgeButton>
            <ForgeButton
              variant="primary"
              disabled={update.isPending}
              onClick={() =>
                update.mutate(
                  { ...switching.pattern, active: !switching.pattern.active },
                  {
                    onSuccess: (saved) => {
                      toast(
                        t(
                          saved.active
                            ? 'shifts.master.toast.activated'
                            : 'shifts.master.toast.deactivated',
                          { name: saved.name },
                        ),
                        'ok',
                      )
                      setSwitching(null)
                      setEditing(null)
                    },
                    onError: () => toast(t('shifts.master.toast.saveFailed'), 'error'),
                  },
                )
              }
            >
              {update.isPending
                ? t('shifts.master.drawer.saving')
                : t(
                    switching?.pattern.active
                      ? 'shifts.master.deactivate'
                      : 'shifts.master.activate',
                  )}
            </ForgeButton>
          </>
        }
      />

      {/* Deleting is soft on the server but has no undo on any screen, and
          `UserShift` rows keep pointing at a pattern that no longer lists. The
          dialog offers the reversible act beside the permanent one, because
          turning a pattern off is what "retire it" usually means. */}
      <ForgeDialog
        open={Boolean(removing)}
        onOpenChange={(open) => (open ? null : setRemoving(null))}
        title={t('shifts.master.deleteTitle')}
        description={t('shifts.master.deleteBody', { name: removing?.name ?? '' })}
        footer={
          <>
            <ForgeButton onClick={() => setRemoving(null)}>
              {t('common:actions.cancel')}
            </ForgeButton>
            <ForgeButton
              disabled={update.isPending}
              onClick={() =>
                update.mutate(
                  { ...removing, active: false },
                  {
                    onSuccess: () => {
                      toast(t('shifts.master.toast.deactivated', { name: removing.name }), 'ok')
                      setRemoving(null)
                      setEditing(null)
                    },
                    onError: () => toast(t('shifts.master.toast.saveFailed'), 'error'),
                  },
                )
              }
            >
              {t('shifts.master.deactivateInstead')}
            </ForgeButton>
            <ForgeButton
              variant="danger"
              disabled={remove.isPending}
              onClick={() =>
                remove.mutate(removing.id, {
                  onSuccess: () => {
                    toast(t('shifts.master.toast.deleted'), 'ok')
                    setRemoving(null)
                    setEditing(null)
                  },
                  onError: () => toast(t('shifts.master.toast.deleteFailed'), 'error'),
                })
              }
            >
              {remove.isPending
                ? t('shifts.master.deleting')
                : t('shifts.master.deleteConfirm')}
            </ForgeButton>
          </>
        }
      />
    </div>
  )
}

/* ══ one pattern ═════════════════════════════════════════════════════════ */

function PatternCard({ pattern, patterns, siteName, editable, onEdit }) {
  const { t } = useTranslation(['app', 'common'])

  const duration = patternDuration(pattern.start, pattern.end)
  const wraps = crossesMidnight(pattern.start, pattern.end)
  const conflicts = overlappingPatterns(pattern, patterns)
  /* Icon and tone are derived from `type`, not stored. See `TYPE_LOOK`. */
  const look = lookFor(pattern.type)

  /* `null` is every plant, and saying so beats printing nothing — an
     account-level pattern is the common case and the one a reader most needs to
     recognise. */
  const scope =
    pattern.plantId == null
      ? t('shifts.master.allSitesScope')
      : (siteName.get(pattern.plantId) ?? pattern.plantId)

  return (
    <Panel className={cn('flex flex-col gap-0', pattern.active ? STRIPE_CLASS[look.tone] : null)}>
      <div className="mb-2.5 flex items-center gap-2">
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-pill px-2.5 py-0.5 text-micro font-semibold',
            TINT_CLASS[look.tone],
            ON_TINT_CLASS[look.tone],
          )}
        >
          <Icon name={look.icon} size="xs" />
          {pattern.name}
        </span>
        <span className="flex-1" />
        <StateBadge tone={pattern.active ? 'green' : null}>
          {t(pattern.active ? 'shifts.master.on' : 'shifts.master.off')}
        </StateBadge>
      </div>

      <Num className="text-metric-sm">
        {(pattern.start || '--:--') + ' – ' + (pattern.end || '--:--')}
      </Num>
      <Num className="mt-0.5 text-micro text-ink-dim">
        {/* The grace figure is gone from this line with the field. */}
        {t('shifts.master.cardMeta', {
          hours: duration.hours,
          minutes: duration.minutes,
        })}
        {wraps ? ` · ${t('shifts.master.wrapsShort')}` : ''}
      </Num>
      <span className="mt-0.5 text-micro text-ink-mute">
        {t(`shifts.master.type.${pattern.type}`)} · {scope}
      </span>

      <div className="mt-3 flex items-center gap-1.5">
        <Eyebrow as="span" className="me-1">
          {t('shifts.master.daysShort')}
        </Eyebrow>
        {DOW_ORDER.map((day) => {
          const on = pattern.days.includes(day)
          return (
            <span
              key={day}
              title={t(`shifts.master.dow.${day}`)}
              className={cn(
                'flex size-5 items-center justify-center rounded-xs border text-micro font-semibold',
                on
                  ? cn('border-transparent', TINT_CLASS[look.tone], ON_TINT_CLASS[look.tone])
                  : 'border-hairline text-ink-mute',
              )}
            >
              {t(`shifts.master.dow.${day}`).charAt(0)}
            </span>
          )
        })}
      </div>

      {conflicts.length ? (
        <p className="mt-2.5 mb-0 text-micro text-on-amber">
          {t('shifts.master.overlaps', { names: conflicts.map((c) => c.name).join(', ') })}
        </p>
      ) : null}

      {/* ── Crew ────────────────────────────────────────────────────────────
          Commented out, not deleted. The demo built this strip because the
          design defines it and then never renders it, and the reasoning stands:
          a pattern with nobody on it is the third thing that goes wrong with a
          rota and is invisible from a list of times.

          What it needs is a read that does not exist. Assignment lives on the
          USER — `UserShift`, written through the full-replace `PUT /users/{id}`
          — so putting it back would mean loading the whole directory, filtering
          it by shift in the client, and writing a person's entire record to add
          them to a crew. One endpoint (`GET /shifts/{id}/users`, or a
          `user_ids` field on `ShiftOut`) restores the whole block.
          See BACKEND-GAPS.md.

          <div className="relative mt-3 border-t border-hairline pt-2.5">
            <Eyebrow as="h3">{t('shifts.master.crew', { total: roster.length })}</Eyebrow>
            … the operator list, the line select and the assign popover …
          </div> */}

      {/* Not offered when the server would refuse it. A plant-level manager sees
          account-level patterns — they apply to their plant and explain their
          roster — but may not change one, so the card says why instead of
          carrying a button that always 403s. */}
      {editable ? (
        <ForgeButton className="mt-3 w-full justify-center" onClick={onEdit}>
          {t('common:actions.edit')}
        </ForgeButton>
      ) : (
        <p className="mt-3 mb-0 flex items-center justify-center gap-1.5 text-micro text-ink-mute">
          <Icon name="lock" size="xs" aria-hidden="true" />
          {t('shifts.master.readOnlyAccountLevel')}
        </p>
      )}
    </Panel>
  )
}

/* The account row ("all sites") and plants. */
const SITE_FILTER_TIERS = new Set(['enterprise', 'site'])
